import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client';
import {
  InsertCauseDocument,
  Parent_Type_Enum,
  UpdateCauseDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalForm } from 'src/components/form/form/ModalForm';
import { useGetIssueById } from 'src/hooks/queries';
import { useGetCauseById } from 'src/hooks/queries/cause/useGetCauseById';
import type { CauseFormFields } from 'src/pages/issues/update/forms/causeSchema';
import {
  CauseFormSchema,
  defaultValues,
} from 'src/pages/issues/update/forms/causeSchema';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { evictField } from '@/utils/graphqlUtils';

import CauseForm from '../../forms/CauseForm';

type Props = {
  onDismiss: (saved: boolean) => void;
  issueId: string;
  causeId?: string;
};

const CauseModal: FC<Props> = ({ onDismiss, issueId, causeId }) => {
  const onUpdate = (cache: ApolloCache<unknown>) => {
    evictField(cache, 'cause');
    evictField(cache, 'cause_aggregate');
  };
  const { t } = useTranslation('common');

  const [insert] = useMutation(InsertCauseDocument, {
    update: onUpdate,
  });
  const [update] = useMutation(UpdateCauseDocument, {
    update: onUpdate,
  });

  const { data: issueData, loading: issueLoading } = useGetIssueById(
    issueId!,
    !issueId
  );
  const parent = issueData?.issue?.[0];
  const { hasPermission: canEditCause, loading: canEditCauseLoading } =
    useHasPermissionQuery('update:cause', parent);
  const { hasPermission: canCreateCause, loading: canCreateCauseLoading } =
    useHasPermissionQuery('insert:cause', parent);

  const { data, loading, error } = useGetCauseById(causeId!, !causeId);

  if (error) {
    throw error;
  }

  const cause = data?.cause[0];
  const canModify = cause
    ? canEditCause && !canEditCauseLoading
    : canCreateCause && !canCreateCauseLoading;

  const onSave = async (data: CauseFormFields) => {
    if (cause) {
      const result = await update({
        variables: {
          ...data,
          ParentIssueId: issueId,
          Id: causeId ?? null,
          OriginalTimestamp: cause?.ModifiedAtTimestamp ?? null,
          CustomAttributeData: data.CustomAttributeData || undefined,
        },
      });
      if (result.data?.update_cause?.affected_rows !== 1) {
        throw new Error(
          'Records not updated. Record may have been updated by another user'
        );
      }
    } else {
      await insert({
        variables: {
          ...data,
          ParentIssueId: issueId,
          CustomAttributeData: data.CustomAttributeData || undefined,
        },
      });
    }
  };

  if (loading || issueLoading) {
    return null;
  }

  return (
    <ModalForm
      onDismiss={onDismiss}
      onSave={onSave}
      defaultValues={defaultValues}
      i18n={t('causes')}
      schema={CauseFormSchema}
      values={cause}
      formId={'cause-form'}
      visible={true}
      readOnly={!canModify}
      parentType={Parent_Type_Enum.Cause}
    >
      <CauseForm readOnly={!canModify} />
    </ModalForm>
  );
};

export default CauseModal;
